import {UI} from '../libs/palette.js';
import {prefersReducedMotion} from '../libs/reducedMotion.js';

const {INK, PAPER, ERROR} = UI;

// Long enough to read twice at this size; it is UI, not gameplay, so a plain
// timer is fine and the game pause doesn't need to hold it.
const SHOW_MS = 6000;
const FADE_MS = 400;

// Below the HUD's clock row (about the top 8% of the stage) and above the
// level card, so it hides nothing the player needs in the first seconds.
const STYLES = `
#offline-notice {
  position: fixed; top: calc(12px + 9vh); left: 50%; transform: translateX(-50%); z-index: 20;
  width: max-content; max-width: min(560px, calc(100vw - 32px)); box-sizing: border-box;
  display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px;
  background: ${PAPER}; color: ${INK}; border: 3px solid ${INK}; box-shadow: 5px 5px 0 ${INK};
  font-family: 'Press Start 2P', monospace; font-size: 9px; line-height: 15px; letter-spacing: 0;
  pointer-events: none; transition: opacity ${FADE_MS}ms linear;
}
#offline-notice.gone { opacity: 0; }
#offline-notice .ico {
  flex: none; width: 15px; height: 15px; background: ${ERROR}; color: #fff;
  display: grid; place-items: center; font-size: 9px;
}
@media (prefers-reduced-motion: reduce) { #offline-notice { transition: none; } }
`;

let styleEl = null;
// while something covers the game (the loading screen), notices wait here
// and show, for their full time, once it has gone
let held = false;
let pending = null;
// the notice on screen: its element, message and timer
let current = null;

/**
 * showOfflineNotice
 * A short-lived note over the game, so a player who starts while the
 * leaderboard is unreachable knows the run won't count. It sits outside the
 * start screen because that closes the moment the run starts. It never takes
 * clicks, so it can't swallow a pounce.
 * @param {string} message
 * @returns {HTMLElement|null} the notice, or null if there was nothing to say
 */
export function showOfflineNotice(message) {
  if (!message) {
    return null;
  }
  if (held) {
    pending = message;
    return null;
  }
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.textContent = STYLES;
    document.head.appendChild(styleEl);
  }
  const old = document.getElementById('offline-notice');
  if (old) {
    old.remove();
  }

  const el = document.createElement('div');
  el.id = 'offline-notice';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.innerHTML = '<span class="ico" aria-hidden="true">!</span><span></span>';
  el.lastElementChild.textContent = message;
  document.body.appendChild(el);

  const timer = setTimeout(() => {
    if (current && current.el === el) {
      current = null;
    }
    if (prefersReducedMotion()) {
      el.remove();
      return;
    }
    el.classList.add('gone');
    setTimeout(() => el.remove(), FADE_MS);
  }, SHOW_MS);
  current = {el, message, timer};
  return el;
}

/**
 * holdNotices
 * Something now covers the game (the loading screen, src/modules/LoadingScreen.js),
 * so a notice would run its time out unseen under it: notices wait until
 * releaseNotices. One already showing is taken down and waits too, to start
 * its time again.
 */
export function holdNotices() {
  held = true;
  if (current) {
    clearTimeout(current.timer);
    current.el.remove();
    pending = current.message;
    current = null;
  }
}

/**
 * releaseNotices
 * The game is in view: a notice that waited shows now, for its full time.
 * @param {Boolean} [drop] true discards it instead (the page is going)
 * @returns {HTMLElement|null} the notice shown, if any
 */
export function releaseNotices(drop) {
  held = false;
  const message = pending;
  pending = null;
  return drop ? null : showOfflineNotice(message);
}

/**
 * dismissOfflineNotice
 * The run will count after all (start_run answered late, Leaderboard.startRun):
 * takes down the notice on screen, and drops one waiting behind the loading
 * screen, so the player isn't told the score won't be saved.
 */
export function dismissOfflineNotice() {
  pending = null;
  if (current) {
    clearTimeout(current.timer);
    current = null;
  }
  if (typeof document === 'undefined') {
    return;
  }
  const el = document.getElementById('offline-notice');
  if (!el) {
    return;
  }
  if (prefersReducedMotion()) {
    el.remove();
    return;
  }
  el.classList.add('gone');
  setTimeout(() => el.remove(), FADE_MS);
}

export default showOfflineNotice;
